import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PAYX } from "../constants/testIds4";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Plus, Trash, ThumbsUp, ThumbsDown, DownloadSimple, CurrencyInr, Receipt, HandCoins, PaperPlaneTilt } from "@phosphor-icons/react";

const ADJ_KINDS = ["arrears", "incentive", "bonus", "loan_disbursed", "loan_installment", "advance_disbursed", "advance_recovery", "overtime", "deduction", "other"];
const CLAIM_STATUS = { submitted: "bg-amber-50 text-amber-800 border-amber-200", approved: "bg-blue-50 text-blue-800 border-blue-200", paid: "bg-emerald-50 text-emerald-800 border-emerald-200", rejected: "bg-red-50 text-red-800 border-red-200" };
const FNF_STATUS = { draft: "bg-zinc-50 text-zinc-700 border-zinc-200", finalized: "bg-blue-50 text-blue-800 border-blue-200", paid: "bg-emerald-50 text-emerald-800 border-emerald-200" };
const fmt = (n, c = "INR") => `${c} ${Number(n || 0).toLocaleString("en-IN")}`;

export default function PayrollAdmin() {
  const { user, tenantId } = useAuth();
  const params = user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {};

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-emerald-700">Payroll · Admin extensions</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Payroll Ops</h1>
        <p className="text-sm text-zinc-500 mt-2">Reimbursement approvals, salary adjustments, loans, full & final settlement, and NACH bank export.</p>
      </div>

      <Tabs defaultValue="reimb">
        <TabsList className="bg-zinc-100 border border-zinc-200 rounded-sm flex-wrap h-auto">
          <TabsTrigger value="reimb" className="rounded-sm" data-testid={PAYX.tabReimb}><Receipt size={14} className="mr-1" /> Reimbursements</TabsTrigger>
          <TabsTrigger value="adj" className="rounded-sm" data-testid={PAYX.tabAdj}><CurrencyInr size={14} className="mr-1" /> Adjustments</TabsTrigger>
          <TabsTrigger value="loans" className="rounded-sm" data-testid={PAYX.tabLoans}><HandCoins size={14} className="mr-1" /> Loans & Advances</TabsTrigger>
          <TabsTrigger value="fnf" className="rounded-sm" data-testid={PAYX.tabFnF}><PaperPlaneTilt size={14} className="mr-1" /> Full & Final</TabsTrigger>
          <TabsTrigger value="bank" className="rounded-sm" data-testid={PAYX.tabBank}><DownloadSimple size={14} className="mr-1" /> Bank export</TabsTrigger>
        </TabsList>

        <TabsContent value="reimb" className="mt-4"><ReimbursementApprovals params={params} /></TabsContent>
        <TabsContent value="adj" className="mt-4"><Adjustments params={params} tenantId={tenantId} user={user} /></TabsContent>
        <TabsContent value="loans" className="mt-4"><Loans params={params} tenantId={tenantId} user={user} /></TabsContent>
        <TabsContent value="fnf" className="mt-4"><FnF params={params} tenantId={tenantId} user={user} /></TabsContent>
        <TabsContent value="bank" className="mt-4"><BankExport tenantId={tenantId} user={user} /></TabsContent>
      </Tabs>
    </div>
  );
}

// ------------- Reimbursement approvals -------------
function ReimbursementApprovals({ params }) {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/reimbursements", { params }).then(({ data }) => setRows(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [params.tenant_id]);
  const decide = async (id, status) => { try { await api.patch(`/reimbursements/${id}`, { status }); toast.success(status); load(); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <div className="border border-zinc-200 rounded-sm overflow-x-auto">
      <table className="hr-table">
        <thead><tr><th>Employee</th><th>Title</th><th>Trip</th><th>Lines</th><th>Total</th><th>Status</th><th>Actions</th></tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-zinc-500 text-sm">No claims.</td></tr>}
          {rows.map((r) => (
            <tr key={r.id}>
              <td><div className="text-sm">{r.employee_name}</div><div className="text-xs font-mono-plex text-zinc-500">{r.employee_code}</div></td>
              <td>{r.title}</td>
              <td className="font-mono-plex text-xs">{r.trip_start_date || "—"} → {r.trip_end_date || "—"}</td>
              <td className="font-mono-plex text-xs">{(r.lines || []).length}</td>
              <td className="font-mono-plex text-xs font-semibold">{Object.entries(r.total_by_currency || {}).map(([c, v]) => <div key={c}>{fmt(v, c)}</div>)}</td>
              <td><Badge variant="outline" className={`rounded-sm ${CLAIM_STATUS[r.status] || ""}`}>{r.status}</Badge></td>
              <td>
                {r.status === "submitted" && (
                  <div className="flex gap-1">
                    <button onClick={() => decide(r.id, "approved")} className="text-xs px-2 py-1 border border-emerald-300 text-emerald-800 hover:bg-emerald-50 rounded-sm" data-testid={`payx-reimb-approve-${r.id}`}><ThumbsUp size={12} className="inline mr-1" /> Approve</button>
                    <button onClick={() => decide(r.id, "rejected")} className="text-xs px-2 py-1 border border-red-300 text-red-700 hover:bg-red-50 rounded-sm"><ThumbsDown size={12} className="inline mr-1" /> Reject</button>
                  </div>
                )}
                {r.status === "approved" && <button onClick={() => decide(r.id, "paid")} className="text-xs px-2 py-1 border border-emerald-300 text-emerald-800 hover:bg-emerald-50 rounded-sm" data-testid={`payx-reimb-pay-${r.id}`}>Mark paid</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ------------- Adjustments -------------
function Adjustments({ params, tenantId, user }) {
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", period_month: period, kind: "incentive", amount: 0, description: "", taxable: true });

  const load = () => api.get("/payroll/adjustments", { params: { ...params, period_month: period } }).then(({ data }) => setRows(data));
  useEffect(() => { load(); api.get("/employees", { params }).then(({ data }) => setEmployees(data)); /* eslint-disable-next-line */ }, [period, tenantId]);

  const save = async () => {
    try {
      const payload = { ...form, amount: parseFloat(form.amount) || 0 };
      if (user?.role === "super_admin" && tenantId) payload.tenant_id = tenantId;
      await api.post("/payroll/adjustments", payload);
      toast.success("Adjustment added");
      setOpen(false);
      setForm({ employee_id: "", period_month: period, kind: "incentive", amount: 0, description: "", taxable: true });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => { if (!window.confirm("Delete adjustment?")) return; try { await api.delete(`/payroll/adjustments/${id}`); load(); } catch (e) { toast.error(formatApiError(e)); } };

  return (
    <div className="space-y-3">
      <div className="flex items-end justify-between gap-3">
        <div>
          <Label className="text-xs">Period (YYYY-MM)</Label>
          <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="rounded-sm w-[160px]" data-testid="adj-period-input" />
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-zenith rounded-sm" data-testid={PAYX.adjCreateBtn}><Plus size={16} className="mr-1.5" /> Add adjustment</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-display text-2xl">Add adjustment</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 space-y-1"><Label className="text-xs">Employee *</Label>
                <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="adj-emp-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} · {e.employee_code}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label className="text-xs">Period</Label><Input type="month" value={form.period_month} onChange={(e) => setForm({ ...form, period_month: e.target.value })} className="rounded-sm" /></div>
              <div className="space-y-1"><Label className="text-xs">Kind</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{ADJ_KINDS.map((k) => <SelectItem key={k} value={k}>{k.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2 space-y-1"><Label className="text-xs">Amount</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-sm font-mono-plex" data-testid="adj-amount-input" /></div>
              <div className="col-span-2 space-y-1"><Label className="text-xs">Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
              <Button onClick={save} className="btn-zenith rounded-sm" data-testid={PAYX.adjSaveBtn}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-zinc-200 rounded-sm overflow-x-auto">
        <table className="hr-table">
          <thead><tr><th>Employee</th><th>Period</th><th>Kind</th><th>Amount</th><th>Description</th><th>Consumed</th><th></th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-zinc-500 text-sm">No adjustments for {period}.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="font-mono-plex text-xs">{r.employee_id?.slice(-6)}</td>
                <td className="font-mono-plex text-xs">{r.period_month}</td>
                <td className="capitalize">{r.kind.replace(/_/g, " ")}</td>
                <td className="font-mono-plex text-sm font-semibold">{fmt(r.amount)}</td>
                <td className="text-xs">{r.description || "—"}</td>
                <td>{r.consumed ? <Badge variant="outline" className="rounded-sm bg-emerald-50 text-emerald-800 border-emerald-200">Yes</Badge> : <Badge variant="outline" className="rounded-sm bg-zinc-50 border-zinc-200">No</Badge>}</td>
                <td>{!r.consumed && <button onClick={() => del(r.id)} className="text-red-600 hover:opacity-70"><Trash size={14} /></button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ------------- Loans -------------
function Loans({ params, tenantId, user }) {
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", principal: 0, interest_rate_percent: 0, tenure_months: 12, start_month: new Date().toISOString().slice(0, 7), reason: "" });

  const load = () => api.get("/loans", { params }).then(({ data }) => setRows(data));
  useEffect(() => { load(); api.get("/employees", { params }).then(({ data }) => setEmployees(data)); /* eslint-disable-next-line */ }, [tenantId]);

  const save = async () => {
    try {
      const payload = { ...form, principal: parseFloat(form.principal), interest_rate_percent: parseFloat(form.interest_rate_percent) || 0, tenure_months: parseInt(form.tenure_months) };
      if (user?.role === "super_admin" && tenantId) payload.tenant_id = tenantId;
      await api.post("/loans", payload);
      toast.success("Loan disbursed");
      setOpen(false);
      setForm({ employee_id: "", principal: 0, interest_rate_percent: 0, tenure_months: 12, start_month: new Date().toISOString().slice(0, 7), reason: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-zenith rounded-sm" data-testid={PAYX.loanCreateBtn}><Plus size={16} className="mr-1.5" /> New loan</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-display text-2xl">Issue loan / advance</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 space-y-1"><Label className="text-xs">Employee *</Label>
                <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="loan-emp-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} · {e.employee_code}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label className="text-xs">Principal ₹</Label><Input type="number" value={form.principal} onChange={(e) => setForm({ ...form, principal: e.target.value })} className="rounded-sm font-mono-plex" data-testid="loan-principal-input" /></div>
              <div className="space-y-1"><Label className="text-xs">Interest %</Label><Input type="number" step="0.1" value={form.interest_rate_percent} onChange={(e) => setForm({ ...form, interest_rate_percent: e.target.value })} className="rounded-sm font-mono-plex" /></div>
              <div className="space-y-1"><Label className="text-xs">Tenure months</Label><Input type="number" value={form.tenure_months} onChange={(e) => setForm({ ...form, tenure_months: e.target.value })} className="rounded-sm font-mono-plex" data-testid="loan-tenure-input" /></div>
              <div className="space-y-1"><Label className="text-xs">Start month</Label><Input type="month" value={form.start_month} onChange={(e) => setForm({ ...form, start_month: e.target.value })} className="rounded-sm" /></div>
              <div className="col-span-2 space-y-1"><Label className="text-xs">Reason</Label><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="rounded-sm" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
              <Button onClick={save} className="btn-zenith rounded-sm" data-testid={PAYX.loanSaveBtn}>Disburse</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-zinc-200 rounded-sm overflow-x-auto">
        <table className="hr-table">
          <thead><tr><th>Employee</th><th>Principal</th><th>Rate</th><th>Tenure</th><th>Installment</th><th>Outstanding</th><th>Status</th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-zinc-500 text-sm">No loans issued.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="font-mono-plex text-xs">{r.employee_id?.slice(-6)}</td>
                <td className="font-mono-plex text-xs font-semibold">{fmt(r.principal)}</td>
                <td className="font-mono-plex text-xs">{r.interest_rate_percent}%</td>
                <td className="font-mono-plex text-xs">{r.tenure_months}m</td>
                <td className="font-mono-plex text-xs">{fmt(r.monthly_installment)}</td>
                <td className="font-mono-plex text-xs font-semibold">{fmt(r.outstanding)}</td>
                <td><Badge variant="outline" className={`rounded-sm ${r.status === "active" ? "bg-blue-50 text-blue-800 border-blue-200" : "bg-zinc-50 border-zinc-200"}`}>{r.status}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ------------- F&F -------------
function FnF({ params, tenantId, user }) {
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", last_working_day: "", notice_period_recovery: 0, leave_encashment: 0, gratuity: 0, bonus: 0, other_earnings: 0, other_deductions: 0, remarks: "" });

  const load = () => api.get("/fnf", { params }).then(({ data }) => setRows(data));
  useEffect(() => { load(); api.get("/employees", { params }).then(({ data }) => setEmployees(data)); /* eslint-disable-next-line */ }, [tenantId]);

  const save = async () => {
    try {
      const payload = { ...form };
      for (const k of ["notice_period_recovery", "leave_encashment", "gratuity", "bonus", "other_earnings", "other_deductions"]) payload[k] = parseFloat(payload[k]) || 0;
      if (user?.role === "super_admin" && tenantId) payload.tenant_id = tenantId;
      await api.post("/fnf", payload);
      toast.success("F&F created");
      setOpen(false);
      setForm({ employee_id: "", last_working_day: "", notice_period_recovery: 0, leave_encashment: 0, gratuity: 0, bonus: 0, other_earnings: 0, other_deductions: 0, remarks: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const transition = async (id, action) => { try { await api.patch(`/fnf/${id}`, { action }); toast.success(action); load(); } catch (e) { toast.error(formatApiError(e)); } };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-zenith rounded-sm" data-testid={PAYX.fnfCreateBtn}><Plus size={16} className="mr-1.5" /> New F&F</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle className="font-display text-2xl">Full & Final settlement</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 space-y-1"><Label className="text-xs">Employee *</Label>
                <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="fnf-emp-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} · {e.employee_code}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label className="text-xs">Last working day *</Label><Input type="date" value={form.last_working_day} onChange={(e) => setForm({ ...form, last_working_day: e.target.value })} className="rounded-sm" data-testid="fnf-lwd-input" /></div>
              <div className="space-y-1"><Label className="text-xs">Notice period recovery</Label><Input type="number" value={form.notice_period_recovery} onChange={(e) => setForm({ ...form, notice_period_recovery: e.target.value })} className="rounded-sm font-mono-plex" /></div>
              <div className="space-y-1"><Label className="text-xs">Leave encashment</Label><Input type="number" value={form.leave_encashment} onChange={(e) => setForm({ ...form, leave_encashment: e.target.value })} className="rounded-sm font-mono-plex" /></div>
              <div className="space-y-1"><Label className="text-xs">Gratuity</Label><Input type="number" value={form.gratuity} onChange={(e) => setForm({ ...form, gratuity: e.target.value })} className="rounded-sm font-mono-plex" /></div>
              <div className="space-y-1"><Label className="text-xs">Bonus</Label><Input type="number" value={form.bonus} onChange={(e) => setForm({ ...form, bonus: e.target.value })} className="rounded-sm font-mono-plex" /></div>
              <div className="space-y-1"><Label className="text-xs">Other earnings</Label><Input type="number" value={form.other_earnings} onChange={(e) => setForm({ ...form, other_earnings: e.target.value })} className="rounded-sm font-mono-plex" /></div>
              <div className="col-span-2 space-y-1"><Label className="text-xs">Other deductions</Label><Input type="number" value={form.other_deductions} onChange={(e) => setForm({ ...form, other_deductions: e.target.value })} className="rounded-sm font-mono-plex" /></div>
              <div className="col-span-2 space-y-1"><Label className="text-xs">Remarks</Label><Textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="rounded-sm" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
              <Button onClick={save} className="btn-zenith rounded-sm" data-testid={PAYX.fnfSaveBtn}>Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-zinc-200 rounded-sm overflow-x-auto">
        <table className="hr-table">
          <thead><tr><th>Employee</th><th>LWD</th><th>Earnings</th><th>Deductions</th><th>Loan recovery</th><th>Net payable</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={8} className="text-center py-6 text-zinc-500 text-sm">No F&F cases.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id}>
                <td><div className="text-sm">{r.employee_name}</div><div className="text-xs font-mono-plex text-zinc-500">{r.employee_code}</div></td>
                <td className="font-mono-plex text-xs">{r.last_working_day}</td>
                <td className="font-mono-plex text-xs">{fmt(r.total_earnings)}</td>
                <td className="font-mono-plex text-xs">{fmt(r.total_deductions)}</td>
                <td className="font-mono-plex text-xs">{fmt(r.loan_outstanding_recovered)}</td>
                <td className="font-mono-plex text-sm font-bold text-emerald-800">{fmt(r.net_payable)}</td>
                <td><Badge variant="outline" className={`rounded-sm ${FNF_STATUS[r.status]}`}>{r.status}</Badge></td>
                <td>
                  {r.status === "draft" && <button onClick={() => transition(r.id, "finalize")} className="text-xs px-2 py-1 border border-blue-300 text-blue-800 hover:bg-blue-50 rounded-sm" data-testid={PAYX.fnfFinalizeBtn(r.id)}>Finalize</button>}
                  {r.status === "finalized" && <button onClick={() => transition(r.id, "mark_paid")} className="text-xs px-2 py-1 border border-emerald-300 text-emerald-800 hover:bg-emerald-50 rounded-sm" data-testid={PAYX.fnfPayBtn(r.id)}>Mark paid</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ------------- Bank export -------------
function BankExport({ tenantId, user }) {
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const payload = { period_month: period };
      if (user?.role === "super_admin" && tenantId) payload.tenant_id = tenantId;
      const { data } = await api.post("/payroll/bank-export", payload);
      setResult(data);
      toast.success(`NACH file generated for ${data.employees} employees`);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const download = async () => {
    try {
      const { data } = await api.get(`/files/${result.storage_path}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/plain" }));
      const a = document.createElement("a"); a.href = url; a.download = `NACH-${period}.txt`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4 max-w-xl">
      <div className="border border-zinc-200 bg-white rounded-sm p-5">
        <div className="text-xs uppercase tracking-widest text-zinc-500">Bank transfer file</div>
        <div className="font-display text-xl font-bold mt-0.5">NACH-style pipe-delimited export</div>
        <p className="text-xs text-zinc-500 mt-1">Generates one row per employee with account, IFSC, and net pay for the selected month. Requires a payroll run for that month.</p>
        <div className="flex items-end gap-2 mt-4">
          <div>
            <Label className="text-xs">Period</Label>
            <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="rounded-sm w-[180px]" data-testid="bank-period-input" />
          </div>
          <Button onClick={run} disabled={busy} className="btn-zenith rounded-sm" data-testid={PAYX.bankExportBtn}>
            <DownloadSimple size={16} className="mr-1.5" /> {busy ? "Generating…" : "Generate NACH file"}
          </Button>
        </div>
        {result && (
          <div className="mt-4 pt-4 border-t border-zinc-100">
            <div className="text-xs text-zinc-500">File ready</div>
            <div className="font-mono-plex text-xs mt-0.5 break-all">{result.storage_path}</div>
            <div className="mt-2 flex items-center gap-3">
              <Badge variant="outline" className="rounded-sm bg-emerald-50 text-emerald-800 border-emerald-200">{result.employees} employees</Badge>
              <Badge variant="outline" className="rounded-sm bg-zinc-50 border-zinc-200 font-mono-plex">Total {fmt(result.total_amount)}</Badge>
            </div>
            <Button size="sm" variant="outline" onClick={download} className="rounded-sm mt-3" data-testid="bank-download-btn"><DownloadSimple size={14} className="mr-1" /> Download</Button>
          </div>
        )}
      </div>
    </div>
  );
}
